import React, { ReactElement, ComponentProps, ComponentType, useCallback, useRef, memo } from "react";
import { VStack, HStack, View } from "../../native-base";
import { Pressable } from "../../Pressable";
import { Icon } from "../../Icon";
import { Divider } from "../../Divider";
import { useResponsiveTextProps, withResponsiveFlags } from "~/hoc/withResponsiveProps";
import { Text, H2 } from "../../Typography";
import { Checkbox, CheckboxGroup, CheckboxProps } from "../../Checkbox";
import { Radio, RadioGroup } from "../../Radio";
import { IHeadingProps, IRadioGroupProps,  ITextProps, IPressableProps, IFormControlProps, ICheckboxGroupProps } from "native-base";
import ResponsiveTitle from "../../ResponsiveTitle";
import { FormControl } from "../../FormControl";
import { renderHTML } from "~/native-ui/utils";

type SelectionObject = {
    value: string,
    title: string,
    description?: string,
    icon?: string,
    iconFamily?: string,
    isChecked: boolean,
    isWidgetGroup?: false,
    isSmallScreen?: boolean,
    RenderComponent?: ComponentType<CheckboxProps>,
}

type SelectionObjectGroup = {
    title: string;
    key: string | number;
    description?: string;
    nodes: SelectionObject[];
    isWidgetGroup: true;
    isSmallScreen?: boolean,
    renderGroup?: (children: ReactElement, group: SelectionObjectGroup) => ReactElement,
}

type OptionGroupProps = SelectionObjectGroup & {
    onChange?: (value: string) => void,
    isReadonly?: boolean,
    value: string[],
    multiple?: boolean,
    hasError?: boolean,
}

type OptionProps = SelectionObject & {
    onChange: (value: string) => void,
    isReadonly?: boolean,
    children?: ReactElement
}

const SingleGroup = RadioGroup as React.ComponentType<IRadioGroupProps & IFormControlProps & { label: string }>;
const MultipleGroup = CheckboxGroup as React.ComponentType<ICheckboxGroupProps & IFormControlProps & { label: string, name: string }>;
const _H2 = H2 as React.ComponentType<IHeadingProps>;
const _Text = Text as React.ComponentType<ITextProps>;
const _Pressable = Pressable as React.ComponentType<IPressableProps & { variant: string }>;


type BigRadioPropsBase = {
    selections: (SelectionObject | SelectionObjectGroup)[],
    trans: (key: string, params?: Record<string, unknown>) => string,
    onChange: (value: any) => void,
    title: string,
    error?: string,
    isinvalid?: boolean,
    tooltip: string,
    multiple?: boolean,
    validationMessage: string,
    description?: string,
    isReadonly?: boolean
    isSmallScreen?: boolean,
};

type Single = BigRadioPropsBase & {
    value: string[],
    multiple: true,
    onChange: (value: string) => void,
}

type Multiple = BigRadioPropsBase & {
    value: string[],
    onChange: (value: string[]) => void,
    multiple: true,
}

type BigRadioProps = Multiple | Single;

export const BigRadio = withResponsiveFlags(memo(function BigRadio (props: BigRadioProps) {
    const value = ([] as string[]).concat(props.value ?? []);
    const { Group, Input } = props.multiple ? {
        Group: MultipleGroup,
        Input: Checkbox,
    } as const : {
        Group: RenderRadio,
        Input: Radio,
    } as const

    const handleChange = useCallback<OptionProps['onChange']>((value) => {
        if (!props.multiple) {
            props.onChange(value ?? null);
            return;
        }
        
        let _value = [].concat(props.value ?? []);
        _value = _value.includes(value) ? _value.filter((x) => x !== value) : _value.concat(value)
        props.onChange(_value.sort());
    }, [props.onChange, props.value, props.multiple,]);

    const error = props.validationMessage || props.error;
    const hasError = !!error || props.isinvalid;

    const groups = props.selections.filter((s): s is SelectionObjectGroup => s.isWidgetGroup === true).concat({
        title: '',
        key: 'single-options',
        nodes: props.selections.filter((s): s is SelectionObject => s.isWidgetGroup !== true),
        isWidgetGroup: true,
    }).filter(({nodes}) => !!nodes.length);

    return (
        <VStack width="100%" space="2">
            {props.title && <ResponsiveTitle title={props.title} />}
            {props.description && <Text color="muted.500">{renderHTML(props.description)}</Text>}
            {
                /** @ts-ignore **/
                error && <FormControl.Label {...errorStyle} label={error} />
            }
            <Group name="" label="" accessibilityLabel=' ' value={value}>
                <VStack space="4" w="100%" >
                {
                    groups.map((option) => (<OptionGroup
                        {...option}
                        key={option.key}
                        value={value}
                        multiple={props.multiple}
                        onChange={handleChange}
                        isReadonly={props.isReadonly} 
                        isSmallScreen={props.isSmallScreen}
                        hasError={hasError}
                    />))
                }
                </VStack>
            </Group>
        </VStack>
    );
}), { includeKeys: ["isSmallScreen"] });

const RenderRadio = (props: Omit<ComponentProps<typeof SingleGroup>, 'value'> & { value: string[] }) => {
    return <SingleGroup {...props} name="" value={props.value[0]} defaultValue={props.defaultValue as unknown as string} />
}

const OptionGroup = memo((props: OptionGroupProps) => {
    const Input = props.multiple ? Checkbox : Radio;
    const divider = <Divider bg={!props.hasError ? undefined : "danger.200" } />;

    const renderGroup = props.renderGroup || ((node) => node);
    const group = <VStack width="100%" space="2">
        {props.title && <ResponsiveTitle title={props.title} />}
        {props.description && <Text color="muted.500">{props.description}</Text>}
        <VStack divider={divider} w="100%" bg={!props.hasError  ? "white" : "danger.50"} borderWidth="1px" borderColor={!props.hasError ? "muted.300" : "danger.300"} rounded="8"  overflow="hidden">
            {
                props.nodes.map((option) => <Option
                    key={option.value}
                    isReadonly={props.isReadonly}
                    onChange={props.onChange}
                    RenderComponent={Input}
                    isChecked={props.value.includes(option.value)}
                    {...option}
                />)
            }
        </VStack>
    </VStack>;
    return renderGroup(group, props)
});


const Option = memo((props: OptionProps) => {
    const preventClick = useRef(false);
    let textProps = useResponsiveTextProps(props.isSmallScreen, 400, 70);

    textProps = props.isSmallScreen ? textProps : {};

    const onPress = React.useCallback((e) => {
        const shouldPrevent = preventClick.current;
        preventClick.current = false;
        
        if (shouldPrevent) return;

        let target = e.target;
        while(e.currentTarget !== target) {
            if (target.tagName === "A") return;
            target = target.parentElement;
        }

        props.onChange(props.value);
        
    }, [props.onChange, props.value]);

    const handledInputChange = React.useCallback((_: boolean) => {
        preventClick.current = true;
        props.onChange(props.value);
    }, [props.onChange, props.value]);

    const iconContainer = props.isSmallScreen ? {} : {px: "3"};
    const stackProps = props.isSmallScreen ? {
        order: 3,
        mt: "3",
        width: "100%",
    } : {
        flex: 1,
    };

    const Input = props.RenderComponent ?? Radio;
    const children = props.isChecked ? props.children : null;

    return (
        <>
            <_Pressable p="4" onPress={props.isReadonly ? undefined : onPress} disabled={props.isReadonly} variant='muted' background={props.isChecked ? "primary.500:alpha.20" : "transparent"}>
                <HStack space="4" alignItems='center' flexWrap='wrap'>
                    {
                        props.icon && 
                        <View {...iconContainer} alignSelf="flex-start">
                            <Icon size="md" type={props.icon} iconFamily={props.iconFamily} />
                        </View>
                    }
                    <VStack space="1" {...stackProps}>
                        <_H2 {...textProps}>{props.title}</_H2>
                        {props.description && renderHTML(`<span>${props.description}</span>`)}
                    </VStack>
                    <View ml="auto">
                        <Input value={props.value} readonly={props.isReadonly} checked={props.isChecked} onChange={props.isReadonly ? undefined : handledInputChange}  />
                    </View>
                </HStack>
            </_Pressable>
            <BigRadioChildren>{children}</BigRadioChildren>
        </>
    )
});


function BigRadioChildren ({ children }) {
    if (!children) return null;
    return <VStack>{children}</VStack>

}



const errorStyle = { color: 'danger.900' };